import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);

  const fetchSlides = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/carousel`);
      setSlides(data);
    } catch (error) {
      console.error('Carousel fetch failed:', error);
    }
  }, []);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goTo = (index) => setCurrent(index);
  const goPrev = () => setCurrent(prev => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setCurrent(prev => (prev + 1) % slides.length);

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  if (slides.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="project-carousel-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-10">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3 block">
            Our Work
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary">
            Project Gallery
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative overflow-hidden rounded-sm shadow-lg" data-testid="project-carousel">
          <div className="relative h-[400px] md:h-[500px]">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className="absolute inset-0 transition-all duration-700 ease-in-out"
                style={{
                  opacity: index === current ? 1 : 0,
                  transform: index === current ? 'scale(1)' : 'scale(1.05)',
                  zIndex: index === current ? 1 : 0,
                }}
                data-testid={`carousel-slide-${index}`}
              >
                <img
                  src={getImageSrc(slide.image_url)}
                  alt={slide.title || `Slide ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {/* Overlay with caption */}
                {(slide.title || slide.caption) && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 md:p-10">
                    <div className="max-w-3xl">
                      {slide.title && <h3 className="text-xl md:text-3xl font-bold text-white mb-2">{slide.title}</h3>}
                      {slide.caption && <p className="text-white/80 text-sm md:text-base">{slide.caption}</p>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {slides.length > 1 && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors"
                data-testid="carousel-prev"
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-colors"
                data-testid="carousel-next"
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Dots */}
          {slides.length > 1 && (
            <div className="absolute bottom-4 right-6 z-10 flex space-x-2" data-testid="carousel-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goTo(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === current ? 'bg-accent w-8' : 'bg-white/50 hover:bg-white/80'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProjectCarousel;
